import React, {
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
} from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useWishlist } from "../../hooks/useWishlist";
import { Heart, Eye, Share2 } from "lucide-react";
import StarRating from "../common/StarRating";
import Badge from "../common/Badge";
import ImageWithFallback from "../common/ImageWithFallback";

import toast from "react-hot-toast";
import { API_BASE_URL } from "../../config/api";

// Optimized image component with lazy loading
const LazyImage = React.memo(
  ({ src, alt, className, fallbackSrc = "/api/placeholder/300/300" }) => {
    const [isLoaded, setIsLoaded] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [currentSrc, setCurrentSrc] = useState(null);
    const imgRef = useRef(null);

    useEffect(() => {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !currentSrc) {
              setCurrentSrc(src || fallbackSrc);
              observer.unobserve(entry.target);
            }
          });
        },
        { rootMargin: "50px" }
      );

      if (imgRef.current) {
        observer.observe(imgRef.current);
      }

      return () => observer.disconnect();
    }, [src, fallbackSrc, currentSrc]);

    const handleLoad = () => setIsLoaded(true);
    const handleError = () => {
      setHasError(true);
      setIsLoaded(true);
    };

    return (
      <div className={`relative ${className}`}>
        {currentSrc && (
          <img
            ref={imgRef}
            src={currentSrc}
            alt={alt || "Product image"}
            className={`w-full h-full object-cover transition-opacity duration-300 ${
              isLoaded ? "opacity-100" : "opacity-0"
            }`}
            onLoad={handleLoad}
            onError={handleError}
            loading="lazy"
          />
        )}
        {!isLoaded && !hasError && (
          <div className="absolute inset-0 bg-gray-200 animate-pulse"></div>
        )}
        {hasError && (
          <div className="absolute inset-0 bg-gray-100 flex items-center justify-center">
            <span className="text-gray-400 text-sm">Image unavailable</span>
          </div>
        )}
      </div>
    );
  }
);

LazyImage.displayName = "LazyImage";

const ProductCard = React.memo(
  ({ product, viewMode = "grid", gridColumns = 4, onQuickView }) => {
    const { user } = useAuth();
    const {
      toggleWishlist,
      isInWishlist,
      loading: wishlistLoading,
    } = useWishlist();

    // Optimized state initialization
    const [selectedVariant, setSelectedVariant] = useState(
      () => product.variants?.find((v) => v.is_default) || product.variants?.[0]
    );
    const [isHovered, setIsHovered] = useState(false);
    const [hoveredButton, setHoveredButton] = useState(null);

    // Memoized pricing calculations with better performance
    const pricing = useMemo(() => {
      const currentPrice =
        selectedVariant?.price_sale ||
        selectedVariant?.price_regular ||
        product.min_price;
      const originalPrice = selectedVariant?.price_regular || product.max_price;
      const hasDiscount =
        selectedVariant?.price_sale &&
        selectedVariant.price_sale < selectedVariant.price_regular;
      const discountPercentage = hasDiscount
        ? Math.round(
            (1 - selectedVariant.price_sale / selectedVariant.price_regular) *
              100
          )
        : 0;

      return { currentPrice, originalPrice, hasDiscount, discountPercentage };
    }, [selectedVariant, product.min_price, product.max_price]);

    const { currentPrice, originalPrice, hasDiscount, discountPercentage } =
      pricing;

    // Simplified image handling - use backend computed primary_image
    const imageData = useMemo(() => {
      // Use the primary_image that's already computed by the backend
      const primaryImage = product.primary_image;

      const getImageUrl = (imageUrl) => {
        if (!imageUrl)
          return "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='400' viewBox='0 0 400 400'%3E%3Crect width='400' height='400' fill='%23f3f4f6'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial, sans-serif' font-size='20' fill='%236b7280'%3ENo Image%3C/text%3E%3C/svg%3E";

        // If it's already a full URL, return as is
        if (imageUrl.startsWith("http")) return imageUrl;
        if (imageUrl.startsWith("/api/placeholder")) return imageUrl;

        const base = (API_BASE_URL || "").replace(/\/+$/, "");
        let cleanUrl = imageUrl
          .replace(/^\/uploads\//, "")
          .replace(/^uploads\//, "");
        return `${base}/uploads/${cleanUrl}`;
      };

      // Handle both object format {url, alt_text} and direct string format
      const imageUrl =
        typeof primaryImage === "string" ? primaryImage : primaryImage?.url;
      const altText =
        typeof primaryImage === "string"
          ? product.name
          : primaryImage?.alt_text || product.name;

      return {
        primaryImage: imageUrl ? getImageUrl(imageUrl) : null,
        altText: altText,
      };
    }, [product]);

    // Memoized stock status
    const stockStatus = useMemo(() => {
      const totalStock = product.total_stock || 0;

      if (totalStock === 0)
        return { status: "out", text: "Out of Stock", color: "red" };
      if (totalStock <= 5)
        return { status: "low", text: "Low Stock", color: "yellow" };
      return { status: "in", text: "In Stock", color: "green" };
    }, [product.total_stock]);

    // Optimized event handlers
    const handleWishlistToggle = useCallback(async () => {
      if (!user) {
        toast.error("Please login to manage wishlist");
        return;
      }

      try {
        await toggleWishlist(product.id);
      } catch (error) {
        toast.error(error.message || "Failed to update wishlist");
      }
    }, [user, product.id, toggleWishlist]);

    const handleQuickView = useCallback(() => {
      if (onQuickView) {
        onQuickView(product);
      }
    }, [onQuickView, product]);

    const handleShare = useCallback(async () => {
      const shareUrl = `${window.location.origin}/products/${product.slug}`;

      if (navigator.share) {
        try {
          await navigator.share({
            title: product.name,
            text: product.description || `Check out ${product.name}`,
            url: shareUrl,
          });
        } catch {
          // Share cancelled or unavailable
        }
      } else {
        // Fallback: copy to clipboard
        try {
          await navigator.clipboard.writeText(shareUrl);
          toast.success("Link copied to clipboard!");
        } catch (error) {
          toast.error("Failed to copy link");
        }
      }
    }, [product]);

    // Memoized card size classes
    const getCardSizeClass = useCallback(() => {
      if (viewMode === "list") return "flex-row";

      switch (gridColumns) {
        case 1:
          return "col-span-1";
        case 2:
          return "col-span-1";
        case 3:
          return "col-span-1";
        case 4:
          return "col-span-1";
        default:
          return "col-span-1";
      }
    }, [viewMode, gridColumns]);

    // Optimized tooltip component - shows on left side of button
    const Tooltip = useCallback(
      ({ text, children }) => (
        <div className="relative">
          {children}
          <div className="absolute right-full top-1/2 transform -translate-y-1/2 mr-2 px-2 py-1 text-xs text-white bg-gray-900 rounded opacity-0 hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10 peer-hover:opacity-100">
            {text}
            <div className="absolute top-1/2 left-full transform -translate-y-1/2 border-4 border-transparent border-l-gray-900"></div>
          </div>
        </div>
      ),
      []
    );

    // Memoized card content to prevent unnecessary re-renders
    const cardContent = useMemo(
      () => (
        <div
          className={`bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden transition-all duration-300 hover:shadow-md hover:border-gray-300 ${getCardSizeClass()} ${
            viewMode === "list" ? "flex" : "h-full flex flex-col"
          } relative group cursor-pointer`}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onClick={(e) => {
            // Don't navigate if clicking on action buttons
            if (e.target.closest("button") || e.target.closest("a")) {
              return;
            }
            // Navigate to product details
            window.location.href = `/products/${product.slug}`;
          }}
        >
          {/* Image Section */}
          <div
            className={`relative ${
              viewMode === "list" ? "w-48 flex-shrink-0" : "aspect-square"
            }`}
          >
            {imageData.primaryImage ? (
              <img
                src={imageData.primaryImage}
                alt={imageData.altText}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.src =
                    "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='400' viewBox='0 0 400 400'%3E%3Crect width='400' height='400' fill='%23f3f4f6'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial, sans-serif' font-size='20' fill='%236b7280'%3ENo Image%3C/text%3E%3C/svg%3E";
                }}
              />
            ) : (
              <ImageWithFallback
                src="/api/placeholder/300/300"
                alt={imageData.altText}
                className="w-full h-full object-cover"
                fallbackSrc="/api/placeholder/300/300"
              />
            )}

            {/* Badges */}
            <div className="absolute top-2 left-2 flex flex-col gap-1">
              {hasDiscount && (
                <Badge color="red" size="sm">
                  -{discountPercentage}%
                </Badge>
              )}
              {product.is_featured && (
                <Badge color="blue" size="sm">
                  Featured
                </Badge>
              )}
              {stockStatus.status === "out" && (
                <Badge color="gray" size="sm">
                  Out of Stock
                </Badge>
              )}
            </div>

            {/* Action Buttons */}
            <div className="absolute top-2 right-2 flex flex-col gap-1">
              {/* Desktop: Show all buttons */}
              <div className="hidden md:flex flex-col gap-1">
                <Tooltip text="Add to Wishlist">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleWishlistToggle();
                    }}
                    disabled={wishlistLoading}
                    className={`peer p-2 rounded-full bg-white/90 backdrop-blur-sm shadow-sm transition-all duration-200 ${
                      isInWishlist(product.id)
                        ? "text-red-500 hover:text-red-600"
                        : "text-gray-600 hover:text-red-500"
                    } ${
                      wishlistLoading
                        ? "opacity-50 cursor-not-allowed"
                        : "hover:scale-110"
                    }`}
                  >
                    <Heart
                      size={16}
                      fill={isInWishlist(product.id) ? "currentColor" : "none"}
                    />
                  </button>
                </Tooltip>

                <Tooltip text="Quick View">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleQuickView();
                    }}
                    className="peer p-2 rounded-full bg-white/90 backdrop-blur-sm shadow-sm text-gray-600 hover:text-blue-600 transition-all duration-200 hover:scale-110"
                  >
                    <Eye size={16} />
                  </button>
                </Tooltip>

                <Tooltip text="Share">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleShare();
                    }}
                    className="peer p-2 rounded-full bg-white/90 backdrop-blur-sm shadow-sm text-gray-600 hover:text-green-600 transition-all duration-200 hover:scale-110"
                  >
                    <Share2 size={16} />
                  </button>
                </Tooltip>
              </div>

              {/* Mobile: Show only Quick View button */}
              <div className="md:hidden">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleQuickView();
                  }}
                  className="p-2 rounded-full bg-white/90 backdrop-blur-sm shadow-sm text-gray-600 hover:text-blue-600 transition-all duration-200 active:scale-95"
                >
                  <Eye size={16} />
                </button>
              </div>
            </div>
          </div>

          {/* Content Section */}
          <div
            className={`p-4 flex-1 ${
              viewMode === "list"
                ? "flex flex-col justify-between"
                : "flex flex-col justify-between"
            }`}
          >
            <div>
              {/* Product Name */}
              <div className="block text-sm font-medium text-gray-900 hover:text-blue-600 transition-colors duration-200 line-clamp-2">
                {product.name}
              </div>

              {/* Brand */}
              {product.brand && (
                <p className="text-xs text-gray-500 mt-1">{product.brand}</p>
              )}

              {/* Rating */}
              {product.avg_rating > 0 && (
                <div className="flex items-center gap-1 mt-2">
                  <StarRating rating={product.avg_rating} size="sm" />
                  <span className="text-xs text-gray-500">
                    ({product.review_count || 0})
                  </span>
                </div>
              )}

              {/* Price with MRP */}
              <div className="flex items-center gap-2 mt-2">
                {hasDiscount ? (
                  <div className="flex flex-col">
                    <span className="text-sm text-gray-500 line-through">
                      MRP ₹{originalPrice?.toLocaleString()}
                    </span>
                    <span className="text-lg font-bold text-gray-900">
                      ₹{currentPrice?.toLocaleString()}
                    </span>
                  </div>
                ) : (
                  <span className="text-lg font-bold text-gray-900">
                    MRP ₹{currentPrice?.toLocaleString()}
                  </span>
                )}
              </div>

              {/* Stock Status */}
              <div className="flex items-center gap-2 mt-2">
                <div
                  className={`w-2 h-2 rounded-full bg-${stockStatus.color}-500`}
                ></div>
                <span className="text-xs text-gray-600">
                  {stockStatus.text}
                </span>
              </div>
            </div>

            {/* Removed Add to Cart Button - Users can add to cart from product detail page */}
          </div>
        </div>
      ),
      [
        viewMode,
        gridColumns,
        getCardSizeClass,
        imageData,
        hasDiscount,
        product,
        stockStatus,
        handleWishlistToggle,
        isInWishlist,
        wishlistLoading,
        handleQuickView,
        handleShare,
        currentPrice,
        originalPrice,
        Tooltip,
      ]
    );

    return cardContent;
  }
);

ProductCard.displayName = "ProductCard";

export default ProductCard;
